import * as React from "react";

import { useState, useCallback, useContext } from "react";

import Section from "./Section";

export type SubpageAltProps = {
    id: string;
    title: string;
};

export interface SubpageProps {
    subpageId?: string;
}

export type SubpageState = {
    subpageId?: string;
};

export type SubpageContextType = SubpageState & {
    setSubpageId: (id: string) => void;
    close: () => void;
};

export const SubpageContext = React.createContext<SubpageContextType>({
    setSubpageId: () => {},
    close: () => {},
});

export const useSubpage = () => useContext(SubpageContext);

const SubpageRenderFn = ({ className = "", children, ...others }: React.HTMLProps<HTMLDivElement>) => {
    const { subpageId } = useSubpage();
    const child = React.Children.toArray(children).find(
        (child) => (child as React.ReactElement).props?.id === subpageId
    );

    return child ? (
        <div className={className} {...others}>
            {child}
        </div>
    ) : (
        <></>
    );
};

const SubpageAltRenderFn = ({
    children,
    className = "",
    title,
    ...others
}: SubpageAltProps & React.HTMLProps<HTMLDivElement>) => {
    const { close } = React.useContext(SubpageContext);
    return (
        <div {...others} className={`flex h-full w-full items-center justify-between ${className}`}>
            <Section className="h-full w-full">
                <Section.Header more={{ action: () => close && close(), text: "Close" }}>
                    {title}
                </Section.Header>
                <Section.Content className="h-full w-full overflow-hidden">
                    {children}
                </Section.Content>
            </Section>
        </div>
    );
};

const Subpage = Object.assign(SubpageRenderFn, {
    Alt: SubpageAltRenderFn,
});

export const withSubpage = <P,>(
    WrappedComponent: React.ComponentType<P>
): React.ComponentType<P> => {
    return (props: P) => {
        const [subpageId, setSubpageId] = useState<string>("");
        const close = useCallback(() => setSubpageId(""), [setSubpageId]);
        return (
            <SubpageContext.Provider value={{ subpageId, setSubpageId, close }}>
                {React.createElement(WrappedComponent as React.ComponentType<any>, props)}
            </SubpageContext.Provider>
        );
    };
};

export default Subpage;
